import { cloneElement, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import type { OrderDto } from '@tikkas/shared';
import { IC, Svg } from '../art/icons.js';
import { api } from '../lib/api.js';
import { fmt, num, whenLabel } from '../lib/format.js';
import { useNav } from '../lib/router.js';
import { emailBonusToCome, featureOn, referralWhat, useStore } from '../lib/store.js';
import { Scroll, Switch, TopBar } from '../components/ui.js';
import { safePrice } from '../lib/bag.js';
import { hasSubscription, pushState, subscribePush, unsubscribePush, type PushState } from '../lib/push.js';
import { RateOrder } from './Messages.js';
import { useLook } from '../lib/look.js';
import { confirmText, type ConfirmResult } from '../lib/joining.js';

/*
 * Account (Saif, 16 Oct 2026): a plain list of rows, each opening its own page, the way Privacy and terms does.
 * Nothing is set on the list itself; Notifications, Saved cards, Saved orders, Order history and Appearance are
 * pages at /account/<page> (AccountPage below). The order of its groups comes from the admin (docs/26); contact and
 * legal, sign-out and deletion always show (Apple requires them).
 */

type Tab = 'all' | 'till' | 'app' | 'catering';
const ROW: React.CSSProperties = { width: '100%', background: 'none', border: 'none', textAlign: 'left', color: 'var(--ink)' };

function Row({ title, sub, onClick, icon, count, danger, last }: { title: string; sub?: string; onClick: () => void; icon?: string; count?: number; danger?: boolean; last?: boolean }) {
  return (
    <button type="button" className="setrow acctrow" style={{ ...ROW, borderBottom: last ? 'none' : '1px solid var(--line)', ...(danger ? { color: 'var(--danger-text)' } : {}) }} onClick={onClick}>
      {icon ? <span className="accent"><Svg html={icon} /></span> : null}
      <span className="grow col" style={{ gap: 2, minWidth: 0 }}><b style={{ fontSize: 14 }}>{title}</b>{sub ? <span className="small muted">{sub}</span> : null}</span>
      {count ? <span className="badgecount">{count}</span> : null}
      <Svg html={IC.chev} />
    </button>
  );
}

const typeLabel = (o: OrderDto) => (o.channel === 'till' ? 'In store' : o.channel === 'catering' ? 'Catering' : `App · ${o.status === 'pending_payment' ? 'unpaid' : o.status.replace('_', ' ')}`);

/** Rows that sit together in one card: your things, then help and your account (with its heading), as today. */
const HELP_ROWS = ['help', 'legal', 'sign_out', 'delete'];
type RowEl = ReactElement<{ last?: boolean }>;
/** A card of rows: the last one has no line under it. */
const lastOff = (rows: RowEl[]) => rows.map((r, i) => (i === rows.length - 1 ? cloneElement(r, { last: true }) : r));

/**
 * The rows in the admin's order (docs/26 §5): neighbouring rows of the same kind share a card, so the starting order
 * draws exactly today's two cards; the heading goes above the first help card.
 */
function Rows({ ids, rows, profile, creator, heading }: { ids: string[]; rows: Record<string, RowEl>; profile: ReactNode; creator?: ReactNode; heading: boolean }) {
  const cards: { kind: 'profile' | 'creator' | 'mine' | 'help'; key: string; rows: RowEl[] }[] = [];
  for (const id of ids) {
    if (id === 'profile') {
      if (profile) cards.push({ kind: 'profile', key: id, rows: [] });
      continue;
    }
    if (id === 'creator') {
      if (creator) cards.push({ kind: 'creator', key: id, rows: [] });
      continue;
    }
    const row = rows[id];
    if (!row) continue;
    const kind = HELP_ROWS.includes(id) ? 'help' : 'mine';
    const prev = cards[cards.length - 1];
    if (prev && prev.kind === kind) prev.rows.push(row);
    else cards.push({ kind, key: id, rows: [row] });
  }
  const firstHelp = cards.find((c) => c.kind === 'help');
  return (
    <>
      {cards.map((c) => {
        if (c.kind === 'profile') return <div key={c.key} style={{ display: 'contents' }}>{profile}</div>;
        if (c.kind === 'creator') return <div key={c.key} style={{ display: 'contents' }}>{creator}</div>;
        const card = <div className="card" key={c.key}>{lastOff(c.rows)}</div>;
        return heading && c === firstHelp ? [<h2 className="h2" style={{ marginTop: 4 }} key={`${c.key}-h`}>Help and your account</h2>, card] : card;
      })}
    </>
  );
}

export function Account() {
  const s = useStore();
  const { navigate, route } = useNav();
  const { v, t } = useLook();
  const u = s.me?.customer;
  const [orders, setOrders] = useState<OrderDto[]>([]);
  useEffect(() => {
    if (u) api<{ orders: OrderDto[] }>('/api/me/orders').then((r) => setOrders(r.orders)).catch(() => undefined);
  }, [u?.id, s.me?.liveOrders.length, s.me?.lastOrder?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const ids = v.pages.account;
  const common: Record<string, RowEl> = {
    help: <Row key="help" title="Contact us" sub="Email, phone or pop in to the shop" onClick={() => navigate('/legal/support')} />,
    legal: <Row key="legal" title="Privacy and terms" sub="Privacy policy, terms of use, store credit, Rewards Club, catering" onClick={() => navigate('/legal')} />,
  };
  if (!u) {
    // Signed out: the sign-in card first, then the rows that work without an account (contact, legal).
    return (
      <Scroll padStyle={{ gap: 18 }}>
        <h1 className="display">{t('page.account')}</h1>
        <div className="card col" style={{ padding: 18, gap: 12 }}><b style={{ fontSize: 16 }}>You're not signed in</b><p className="muted" style={{ margin: 0, fontSize: 14 }}>Sign in with your mobile number to order, earn points and see your history.</p><button type="button" className="yellow" onClick={() => navigate('/join?next=/account')}>Sign in or join</button></div>
        <Rows ids={ids} rows={common} profile={null} heading={false} />
      </Scroll>
    );
  }
  const rateFor = route.search.get('rate') ? orders.find((o) => o.status === 'collected') ?? s.me?.lastOrder ?? null : null;
  const saved = s.me?.savedOrders.length ?? 0;
  const cards = u.paymentMethods.length;
  const alerts = [u.consents.push_updates ? 'order updates on' : 'order updates off', u.consents.push || u.consents.email || u.consents.sms ? 'offers on' : 'offers off'].join(' · ');
  // The Creator hub row (Prototype D; CLAUDE.md §4 "Creators"): only creators see it; the admin can move or switch it off.
  const creator = u.group === 'creator' ? (
    <button type="button" className="bannerx" onClick={() => navigate('/creator')}>
      <span style={{ color: 'var(--violet)' }}><Svg html={IC.spark} /></span>
      <span className="grow col" style={{ gap: 2 }}><b style={{ fontSize: 14 }}>Creator hub</b><span className="small soft">Your code, earnings, credit and giveaways</span></span>
      <Svg html={IC.chev} />
    </button>
  ) : null;
  const profile = (
    <>
      <button type="button" className="card row" style={{ padding: 14, gap: 14, textAlign: 'left', width: '100%', color: 'var(--ink)' }} onClick={() => s.openSheet({ kind: 'name' })}>
        <span className="avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{u.initials}</span>
        <span className="grow col" style={{ gap: 4 }}><b style={{ fontSize: 15 }}>{`${u.firstName} ${u.lastName}`.trim() || 'Add your name'}</b><span className="small muted tnum">{u.phoneDisplay} · verified{u.email ? ` · ${u.email}` : ''}</span>{u.group !== 'member' && <span><span className="badge violet">{u.group === 'founder' ? 'Founders Club' : u.group === 'creator' ? 'Creator' : u.group === 'staff' ? 'Staff' : u.group}</span></span>}</span>
        <Svg html={IC.chev} />
      </button>
      <EmailBonus />
      {rateFor && <RateOrder orderId={rateFor.id} number={rateFor.number} />}
    </>
  );
  const rows: Record<string, RowEl> = {
    ...common,
    orders: <Row key="orders" title="Order history" sub={orders.length ? `${orders.length} order${orders.length === 1 ? '' : 's'}` : 'Your orders will show here'} onClick={() => navigate('/account/orders')} />,
    saved: <Row key="saved" title="Saved orders" sub={saved ? `${saved} saved` : 'Save an order from its screen to reorder in one tap'} onClick={() => navigate('/account/saved')} />,
    cards: <Row key="cards" title="Saved cards" sub={cards ? `${cards} card${cards === 1 ? '' : 's'}` : 'None saved yet'} onClick={() => navigate('/account/cards')} />,
    notifications: <Row key="notifications" title="Notifications" sub={alerts} onClick={() => navigate('/account/notifications')} />,
    messages: <Row key="messages" title="Messages" sub="Offers, news and the odd thank-you" count={s.me?.unseenMessages ?? 0} onClick={() => navigate('/messages')} />,
    invite: <Row key="invite" title="Invite a friend" sub={`You both get ${referralWhat(s)}`} onClick={() => navigate('/invite')} />,
    sign_out: <Row key="sign_out" title="Sign out" onClick={async () => { await s.signOut(); navigate('/', { root: true }); }} />,
    // Apple requires deleting the account inside the app: this row, then "Delete my account" in the sheet (two taps).
    delete: <Row key="delete" title="Delete account" sub="Removes your account and history straight away" danger onClick={() => s.openSheet({ kind: 'delete' })} />,
  };
  return (
    <Scroll padStyle={{ gap: 18 }}>
      <h1 className="display">{t('page.account')}</h1>
      <Rows ids={ids} rows={rows} profile={profile} creator={creator} heading />
    </Scroll>
  );
}

/**
 * "Join our email list and get N bonus points" (Saif, 24 Oct 2026): one row, shown while the bonus is still to come; it
 * opens the email-list sheet (components/Sheets.tsx EmailListSheet). A plain email for receipts, with no bonus and no
 * consent, lives in the profile sheet.
 */
function EmailBonus() {
  const s = useStore();
  const { t } = useLook();
  const u = s.me?.customer;
  const bonus = emailBonusToCome(s);
  if (!u || bonus <= 0) return null;
  return (
    <section className="card rows" aria-label="Join our email list">
      <button type="button" onClick={() => s.openSheet({ kind: 'emailList' })}><span className="grow col" style={{ gap: 2 }}><b>{t('earn.email_list').replace('{bonus}', num(bonus))}</b><span className="s">{u.email && !u.emailConfirmed ? `Confirm ${u.email} to join, or use another email` : 'Offers and news now and then. Unsubscribe any time.'}</span></span><Svg html={IC.chev} /></button>
    </section>
  );
}

/** The page the confirmation email's button opens (/account/email?t=…): no sign-in needed (docs/23 §3.6). */
function EmailConfirmPage() {
  const s = useStore();
  const { route, navigate } = useNav();
  const [done, setDone] = useState<{ r: ConfirmResult | null; e: unknown } | null>(null);
  const asked = useRef(false);
  useEffect(() => {
    if (asked.current) return; // once per page, so a second call can't turn "confirmed" into "already confirmed"
    asked.current = true;
    const token = route.search.get('t') ?? '';
    if (!token) return setDone({ r: null, e: { status: 404, code: 'token_unknown', message: '' } });
    api<ConfirmResult>('/api/email/confirm', { body: { token } })
      .then((r) => {
        setDone({ r, e: null });
        history.replaceState(history.state, '', '/account/email'); // the link is spent; keep it out of history
        if (s.me) s.refreshMe().catch(() => undefined);
      })
      .catch((e: unknown) => setDone({ r: null, e }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const text = done ? confirmText(done.r, done.e) : null;
  return (
    <Scroll padStyle={{ gap: 18, paddingTop: 16 }}>
      <TopBar title="Your email" />
      <div className="card empty" role="status">
        {!text ? <b>Confirming…</b> : <><b style={{ fontSize: 17 }}>{text.title}</b><span className="small muted">{text.body}</span>
          <button type="button" className="yellow" style={{ minHeight: 50, padding: '0 24px' }} onClick={() => navigate(text.good ? '/card' : '/account', { root: true })}>{text.good ? 'Open your card' : 'Go to Account'}</button></>}
      </div>
    </Scroll>
  );
}

/** The pages behind the Account rows: /account/notifications, /account/cards, /account/saved, /account/orders. */
export function AccountPage() {
  const s = useStore();
  const { route } = useNav();
  const page = route.params.id ?? '';
  if (page === 'email') return <EmailConfirmPage />;
  return <SignedInPage page={page} />;
}

const PAGES = ['notifications', 'cards', 'saved', 'orders'];

/** The pages that need an account. Redirects wait for the first /api/me answer, so a link straight to one doesn't bounce a signed-in customer. */
function SignedInPage({ page }: { page: string }) {
  const s = useStore();
  const { navigate } = useNav();
  const u = s.me?.customer;
  const to = !PAGES.includes(page) ? '/account' : s.meLoaded && !u ? `/join?next=/account/${page}` : null;
  useEffect(() => {
    if (to) navigate(to, { replace: true });
  }, [to]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!u || to) return null;
  if (page === 'notifications') return <NotificationsPage />;
  if (page === 'cards') return <CardsPage />;
  if (page === 'saved') return <SavedOrdersPage />;
  return <OrdersPage />;
}

function NotificationsPage() {
  const s = useStore();
  const u = s.me!.customer;
  const [push, setPush] = useState<{ state: PushState; subscribed: boolean }>({ state: pushState(), subscribed: false });
  // Read again when a sheet closes: Turn on here opens our notifications screen first (it names offers) while the phone is undecided.
  useEffect(() => {
    hasSubscription().then((subscribed) => setPush({ state: pushState(), subscribed })).catch(() => undefined);
  }, [u.id, s.sheet?.kind]);
  const consent = async (channel: 'email' | 'sms' | 'push' | 'push_updates', v: boolean) => {
    try {
      await api('/api/me/consents', { method: 'PUT', body: { [channel]: v } });
      await s.refreshMe();
    } catch (e) {
      s.toast(e instanceof Error ? e.message : 'Could not save');
    }
  };
  return (
    <Scroll padStyle={{ gap: 18, paddingTop: 16 }}>
      <TopBar title="Notifications" />
      <div className="card">
        <div className="setrow"><span className="grow col" style={{ gap: 2 }}><b style={{ fontSize: 14 }}>Notifications on this phone</b><span className="small muted">{push.state === 'needs_home_screen' ? 'Add Tikkas to your home screen (Share → Add to Home Screen), then switch this on there.' : push.state === 'unsupported' ? 'Not available in this browser.' : push.state === 'denied' ? 'Blocked in your phone settings for this site.' : push.subscribed ? 'On. "Your order is ready" pings land here.' : 'Get a ping the moment your order is ready.'}</span></span>
          {(push.state === 'default' || push.state === 'granted') && (push.subscribed ? <button type="button" className="pillbtn" onClick={async () => { await unsubscribePush(); setPush({ state: pushState(), subscribed: false }); s.toast('Notifications off on this phone'); }}>Turn off</button> : <button type="button" className="pillbtn" onClick={async () => { if (push.state === 'default') { s.openSheet({ kind: 'notifications', mode: 'ask', place: 'home' }); return; } const r = await subscribePush(); setPush({ state: pushState(), subscribed: r === 'subscribed' }); s.toast(r === 'subscribed' ? 'Notifications on' : r === 'denied' ? 'Notifications were not allowed' : r === 'not_configured' ? 'Push is not set up on this server yet' : 'Could not switch notifications on'); await s.refreshMe(); }}>Turn on</button>)}
        </div>
        <div className="setrow"><span className="grow col" style={{ gap: 2 }}><b style={{ fontSize: 14 }}>Order updates by push</b><span className="small muted">"Your order is ready" and nothing else. Switched on when you allowed notifications. Not marketing.</span></span><Switch on={u.consents.push_updates} label="Order updates by push" onChange={(v) => consent('push_updates', v)} /></div>
        <div className="setrow"><span className="grow col" style={{ gap: 2 }}><b style={{ fontSize: 14 }}>Offers and news by push</b><span className="small muted">New flavours, offers and your points first. Switched on when you allowed notifications; turn it off here any time.</span></span><Switch on={u.consents.push} label="Offers by push" onChange={(v) => consent('push', v)} /></div>
        <div className="setrow"><span className="grow col" style={{ gap: 2 }}><b style={{ fontSize: 14 }}>Offers and news by email</b><span className="small muted">{u.email ? u.email : 'Add an email to your profile first'}</span></span><Switch on={u.consents.email} label="Offers by email" onChange={(v) => consent('email', v)} /></div>
        <div className="setrow"><span className="grow col" style={{ gap: 2 }}><b style={{ fontSize: 14 }}>Offers and news by text</b><span className="small muted">Rare, for big launches only. Reply STOP to any text to stop them.</span></span><Switch on={u.consents.sms} label="Offers by text" onChange={(v) => consent('sms', v)} /></div>
        <div className="setrow"><span className="small muted">Every change here is logged with the time and where it was made. Order updates and receipts are never marketing.</span></div>
      </div>
    </Scroll>
  );
}

function CardsPage() {
  const s = useStore();
  const u = s.me!.customer;
  return (
    <Scroll padStyle={{ gap: 18, paddingTop: 16 }}>
      <TopBar title="Saved cards" />
      {u.paymentMethods.length ? (
        <div className="card">{u.paymentMethods.map((c) => <div className="setrow" key={c.id}><span className="grow col" style={{ gap: 2 }}><b style={{ fontSize: 14 }}>{c.label}</b>{c.expMonth ? <span className="small muted">Expires {String(c.expMonth).padStart(2, '0')}/{String(c.expYear).slice(-2)}</span> : null}</span><button type="button" className="pillbtn" onClick={async () => { await api(`/api/me/payment-methods/${c.id}`, { method: 'DELETE' }).catch(() => undefined); await s.refreshMe(); s.toast('Card removed'); }}>Remove</button></div>)}</div>
      ) : <div className="card empty"><b>No saved cards</b><span className="small muted">Tick "Remember this card" when you pay and it shows here.</span></div>}
      <p className="muted" style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5 }}>Cards are kept by Stripe, our payment provider. We only see the brand and the last four digits.</p>
    </Scroll>
  );
}

function SavedOrdersPage() {
  const s = useStore();
  const { navigate } = useNav();
  const { t } = useLook();
  const menu = s.menuData?.menu;
  const saved = s.me?.savedOrders ?? [];
  return (
    <Scroll padStyle={{ gap: 18, paddingTop: 16 }}>
      <TopBar title="Saved orders" />
      {saved.length && menu ? (
        <div className="card rows">{saved.map((f) => {
          const total = f.lines.reduce((a, l) => a + (safePrice(menu, { id: 'x', config: l.config, qty: l.qty })?.linePence ?? 0), 0);
          const reorder = () => {
            for (const l of f.lines) s.addLine({ ...l.config, reward: null }, l.qty);
            navigate('/bag');
          };
          return <div className="r" key={f.id}><span className="ic"><Svg html={IC.starSm} /></span><span className="grow col" style={{ gap: 2, minWidth: 0 }}><b>{f.name}</b><span className="s">{f.lines.map((l) => `${menu.items[l.config.itemId]?.name ?? ''}${l.qty > 1 ? ` × ${l.qty}` : ''}`).join(' + ')} · {fmt(total)}</span></span><button type="button" className="pillbtn" onClick={reorder}>Reorder</button><button type="button" className="pillbtn line" onClick={async () => { await api(`/api/me/saved-orders/${f.id}`, { method: 'DELETE' }); await s.refreshMe(); s.toast('Removed from saved orders'); }}>Remove</button></div>;
        })}</div>
      ) : <div className="card empty"><b>{t('saved.empty')}</b><span className="small muted">After an order, tap "Save this order" and give it a name. It then shows here and on your home screen.</span></div>}
    </Scroll>
  );
}

function OrdersPage() {
  const s = useStore();
  const { navigate } = useNav();
  const { t } = useLook();
  const u = s.me!.customer;
  const [tab, setTab] = useState<Tab>('all');
  const [orders, setOrders] = useState<OrderDto[]>([]);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  useEffect(() => {
    api<{ orders: OrderDto[] }>('/api/me/orders').then((r) => setOrders(r.orders)).catch(() => undefined);
  }, [u.id, s.me?.liveOrders.length, s.me?.lastOrder?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const tabs: [Tab, string][] = [['all', 'All'], ['till', 'In store'], ['app', 'App']];
  if (featureOn(s, 'catering') || orders.some((o) => o.channel === 'catering')) tabs.push(['catering', 'Catering']);
  const list = orders.filter((o) => tab === 'all' || o.channel === tab);
  return (
    <Scroll padStyle={{ gap: 18, paddingTop: 16 }}>
      <TopBar title="Order history" />
      <div className="pills" role="tablist" aria-label="Filter orders">{tabs.map(([k, l]) => <button type="button" role="tab" className="pill" key={k} aria-selected={tab === k} onClick={() => setTab(k)}>{l}</button>)}</div>
      {list.length ? list.map((o) => {
        const hot = ['placed', 'preparing', 'ready'].includes(o.status);
        const isOpen = !!open[o.id];
        return (
          <button type="button" className={`order ${hot ? 'hot' : ''}`} key={o.id} aria-expanded={isOpen} onClick={() => (hot ? navigate(`/order/${o.id}`) : setOpen({ ...open, [o.id]: !isOpen }))}>
            <span className="row" style={{ gap: 8 }}><span className={`badge ${o.channel === 'till' ? '' : hot ? 'good' : 'out'}`}>{typeLabel(o)}</span><span className="muted" style={{ fontSize: 13 }}>{whenLabel(o.placedAt ?? o.readyAt)} · #{o.number}</span></span>
            <span style={{ fontSize: 14 }}>{o.lines.map((l) => `${l.title}${l.qty > 1 ? ` × ${l.qty}` : ''}`).join(', ')}</span>
            {o.group ? <span className="small muted">{o.group.role === 'member' ? `Part of ${o.group.hostName}'s group order · ${o.group.myLineIds?.length ?? 0} item${o.group.myLineIds?.length === 1 ? '' : 's'} yours` : `Group order · ${o.group.people} people`}</span> : null}
            <span className="sum" style={{ width: '100%' }}><span>{o.group?.role === 'member' ? `${o.group.hostName} paid` : o.payLabel}{o.totalPence ? ` · ${fmt(o.totalPence)}` : ''}{o.refundedPence ? ` · refunded ${fmt(o.refundedPence)}` : ''}</span><span className="accent" style={{ fontWeight: 600 }}>{o.group?.role === 'member' ? (o.status === 'collected' ? `+${num(o.group.myPoints ?? 0)}` : hot ? `+${num(o.group.myPoints ?? 0)} on collection` : '') : o.pointsEarned != null ? `+${num(o.pointsEarned)}` : hot ? `+${num(o.pointsToEarn)} on collection` : ''}{o.pointsSpent && o.group?.role !== 'member' ? ` · −${num(o.pointsSpent)} used` : ''}</span></span>
            {isOpen && <span className="lines">{o.lines.map((l) => <span className="sum" style={{ fontSize: 14, color: 'var(--ink)' }} key={l.id}><span className="col" style={{ gap: 2 }}><b>{l.title}{l.qty > 1 ? ` × ${l.qty}` : ''}</b>{l.description ? <span className="small muted">{l.description}</span> : null}</span><span className="tnum">{l.linePence === 0 ? 'Free' : fmt(l.linePence)}</span></span>)}{o.channel === 'till' ? <span className="small muted">Added to your history when you scanned your code at the till.</span> : null}</span>}
          </button>
        );
      }) : <div className="card empty"><b>{tab === 'catering' ? 'No catering orders yet' : t('orders.empty_title')}</b><span className="small muted">{tab === 'catering' ? 'Office lunches and party orders will show here.' : t('orders.empty_body')}</span></div>}
      <p className="muted" style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5 }}>Orders you pay for in the shop appear here when you scan your code at the till, with what you had, how you paid and the points you earned.</p>
    </Scroll>
  );
}
